import {CSSProperties} from 'react';
import {alpha, styled, useTheme} from '@mui/material/styles';
import {Handle, HandleType, Position, useNodeId, useStore} from 'reactflow';

interface FlowHandleProps {
    type: HandleType;
    position: Position;
    id?: string;
    color: string;
    style?: CSSProperties;
}

const RESTING_SIZE = 14;

const VALID_SIZE = 20;

const DIMMED_OPACITY = 0.3;

const TRANSITION = 'width 140ms ease-out, height 140ms ease-out, opacity 140ms ease-out, background-color 140ms ease-out, box-shadow 140ms ease-out';

const StyledHandle = styled(Handle, {
    shouldForwardProp: prop => prop !== 'handleColor' && prop !== 'isValidTarget'
})<{handleColor: string; isValidTarget?: boolean}>(({handleColor, isValidTarget}) => ({
    border: '2px solid #ffffff',
    zIndex: 5,
    // Invisible expanded hit target (8px all around) so starting an edge doesn't require pixel-perfection
    '&::before': {
        content: '""',
        position: 'absolute',
        top: -8,
        left: -8,
        right: -8,
        bottom: -8,
        borderRadius: '50%',
        cursor: 'crosshair',
    },
    '&:hover': {
        zIndex: 10,
        boxShadow: isValidTarget
            ? undefined
            : `0 0 0 4px ${alpha(handleColor, 0.35)}`,
    },
}));

const FlowHandle = ({type, position, id, color, style}: FlowHandleProps) => {
    const theme = useTheme();
    const nodeId = useNodeId();

    const origin = useStore(state => state.connectionStartHandle);

    const isOrigin = !!origin
        && origin.nodeId === nodeId
        && origin.type === type
        && (origin.handleId ?? null) === (id ?? null);

    const isValidTarget = !!origin && origin.type !== type && origin.nodeId !== nodeId;

    const isDimmed = !!origin && !isOrigin && !isValidTarget;

    const size = isValidTarget ? VALID_SIZE : RESTING_SIZE;
    const accent = theme.palette.success.main;

    return (
        <StyledHandle
            type={type}
            position={position}
            id={id}
            handleColor={color}
            isValidTarget={isValidTarget}
            style={{
                width: size,
                height: size,
                background: isValidTarget ? accent : color,
                opacity: isDimmed ? DIMMED_OPACITY : 1,
                boxShadow: isValidTarget ? `0 0 0 6px ${alpha(accent, 0.25)}` : undefined,
                cursor: 'crosshair',
                transition: TRANSITION,
                ...style,
            }}
        />
    );
};

export default FlowHandle;
